import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { DocEntry } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';

@customElement('cp-docs-viewer')
export class CpDocsViewer extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;

  @state() private _entries: DocEntry[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId') && this.projectId > 0) {
      void this._loadDocs();
    }
  }

  private async _loadDocs(): Promise<void> {
    if (!this.projectId) return;
    this._loading = true;
    this._error = '';

    try {
      this._entries = await projectApi.getDocs(this.projectId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Dokumentation konnte nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _getIcon(entry: DocEntry): string {
    if (entry.type === 'directory') return '\u{1F4C1}';
    if (entry.name.endsWith('.md')) return '\u{1F4DD}';
    return '\u{1F4C4}';
  }

  private _renderEntry(entry: DocEntry, depth = 0): TemplateResult {
    const indent = depth * 16;
    return html`
      <div class="cp-docs-viewer__entry" style="padding-left: ${indent}px">
        <span class="cp-docs-viewer__icon">${this._getIcon(entry)}</span>
        <span class="cp-docs-viewer__name">${entry.name}</span>
        <span class="cp-docs-viewer__path">${entry.path}</span>
      </div>
      ${entry.children?.map(child => this._renderEntry(child, depth + 1)) ?? ''}
    `;
  }

  private _renderSkeleton() {
    return html`
      <div class="cp-docs-viewer__items">
        ${[1, 2, 3].map(() => html`
          <div class="cp-docs-viewer__skeleton">
            <div class="cp-docs-viewer__skeleton-line cp-docs-viewer__skeleton-line--name"></div>
            <div class="cp-docs-viewer__skeleton-line cp-docs-viewer__skeleton-line--path"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-docs-viewer__empty">
        <p class="cp-docs-viewer__empty-text">Keine Dokumentation vorhanden</p>
      </div>
    `;
  }

  render() {
    if (this._loading) return this._renderSkeleton();

    if (this._error) {
      return html`<p class="cp-docs-viewer__error">${this._error}</p>`;
    }

    if (this._entries.length === 0) return this._renderEmpty();

    return html`
      <div class="cp-docs-viewer">
        <div class="cp-docs-viewer__items">
          ${this._entries.map(entry => this._renderEntry(entry))}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-docs-viewer': CpDocsViewer;
  }
}
